/**
 * NotificationCenter — renders list of notifications with dismiss/action.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { Notification } from '@/infra/types/domain';
import { RenderDisplayable } from '@/features/predicate/RenderDisplayable';
import type { Displayable } from '@/features/predicate/renderSegments';

export interface NotificationCenterProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  onAction?: (notification: Notification) => void;
  autoDismissMs?: number;
}

export function NotificationCenter({
  notifications,
  onDismiss,
  onAction,
  autoDismissMs = 3000,
}: NotificationCenterProps) {
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Auto-dismiss new notifications. Per-notification override
  // (`auto_dismiss_ms`) wins; otherwise the prop default applies.
  // A non-positive value disables auto-dismiss for that row.
  //
  // Timers must NOT be torn down on every effect re-run. The
  // `notifications` array gets a new identity on any Redux update
  // (immutable reducers return fresh arrays), so a naive cleanup
  // would clear the in-flight setTimeout and the next run would
  // schedule a fresh one — resetting the dismiss clock indefinitely
  // and stretching a "5s" toast into "5s × number of unrelated
  // dispatches". We only clear timers for notifications that
  // disappeared from the list.
  useEffect(() => {
    const liveIds = new Set(notifications.map((n) => n.id));
    for (const [id, timer] of Array.from(timersRef.current.entries())) {
      if (!liveIds.has(id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    }
    for (const notification of notifications) {
      const dismissMs = notification.auto_dismiss_ms ?? autoDismissMs;
      if (dismissMs <= 0) continue;
      if (
        notification.status === 'unread' &&
        !timersRef.current.has(notification.id)
      ) {
        const id = notification.id;
        const timer = setTimeout(() => {
          onDismiss(id);
          timersRef.current.delete(id);
        }, dismissMs);
        timersRef.current.set(id, timer);
      }
    }
  }, [notifications, autoDismissMs, onDismiss]);

  // Unmount-only cleanup: clear all live timers so we don't leak
  // setTimeout handles when the host view tears down.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const handleClick = useCallback(
    (notification: Notification) => {
      if (onAction && notification.action_url) {
        onAction(notification);
      }
    },
    [onAction]
  );

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div
      className="notification-center"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      <ul className="notification-center__list" role="list">
        {notifications.map((notification) => (
          <li
            key={notification.id}
            className={`notification-center__item notification-center__item--${notification.status}`}
            role="listitem"
          >
            <div
              className="notification-center__content"
              onClick={() => handleClick(notification)}
              role={notification.action_url ? 'button' : undefined}
              tabIndex={notification.action_url ? 0 : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && notification.action_url) {
                  handleClick(notification);
                }
              }}
            >
              <span className="notification-center__title">
                {notification.title}
              </span>
              {notification.body_runs && notification.body_runs.length > 0 ? (
                <RenderDisplayable
                  runs={notification.body_runs as Displayable}
                  className="notification-center__message"
                />
              ) : (
                <span className="notification-center__message">
                  {notification.message}
                </span>
              )}
            </div>
            <button
              className="notification-center__dismiss"
              onClick={() => onDismiss(notification.id)}
              aria-label={`Dismiss notification: ${notification.title}`}
            >
              &times;
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
